import { useEffect, useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import { apiClient } from "../../constants.ts";
import {
  Button,
  Collapsible,
  customConfirm,
  inputBaseClass,
} from "./components.tsx";
import { useAuth, useQuery } from "./db.ts";

type TankResult = {
  name: string;
  description: string;
  latestVersion: string;
  scanVerdict: string;
  publisher: string;
  downloads: number;
};

const useDebounced = (value: string, ms: number) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
};

const versionBadgeStyle =
  "inline-block ml-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-surface-overlay-light dark:bg-surface-overlay text-text-tertiary-light dark:text-text-tertiary";

const SkillRow = (
  { sourceUrl, installedAt, version, onUninstall }: {
    sourceUrl: string;
    installedAt: number;
    version?: string;
    onUninstall: () => void;
  },
) => (
  <div className="flex items-center justify-between p-2 rounded border border-border-subtle-light dark:border-border-subtle bg-surface-overlay-light dark:bg-surface-overlay text-sm">
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="font-mono text-xs truncate" title={sourceUrl}>
        {sourceUrl}
        {version && <span className={versionBadgeStyle}>v{version}</span>}
      </span>
      <span className="text-xs text-text-tertiary-light dark:text-text-tertiary">
        Installed {new Date(installedAt).toLocaleDateString()}
      </span>
    </div>
    <Button
      onClick={onUninstall}
      className="bg-danger hover:bg-danger-hover text-xs px-2 py-1 shrink-0 ml-2"
    >
      Uninstall
    </Button>
  </div>
);

const tankBadgeStyle =
  "inline-block ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400";

const TankResultRow = (
  { result, onInstall }: { result: TankResult; onInstall: () => void },
) => (
  <button
    type="button"
    onClick={onInstall}
    className="w-full text-left px-3 py-2 hover:bg-surface-overlay-light dark:hover:bg-surface-overlay border-b border-border-subtle-light dark:border-border-subtle last:border-b-0 cursor-pointer"
  >
    <div className="font-medium text-sm">
      {result.name}
      <span className={tankBadgeStyle}>{result.scanVerdict}</span>
    </div>
    <div className="text-xs text-text-tertiary-light dark:text-text-tertiary truncate">
      {result.description}
    </div>
  </button>
);

export const CommunitySkillsConfig = (
  { id, botId, hideCollapsible }: {
    id: string;
    botId: string;
    hideCollapsible?: boolean;
  },
) => {
  const auth = useAuth();
  const token = auth.user?.refresh_token ?? "";
  const { data } = useQuery({ bots: { $: { where: { id } }, skills: {} } });
  const skills: {
    id: string;
    sourceUrl: string;
    installedAt: number;
    version?: string;
  }[] = data?.bots?.[0]?.skills ?? [];

  const [input, setInput] = useState("");
  const [tankResults, setTankResults] = useState<TankResult[]>([]);
  const [searching, setSearching] = useState(false);
  const debouncedQuery = useDebounced(input, 300);

  const isSearchable = input.trim().length > 0 && !input.startsWith("http");
  const showSearching = searching ||
    (isSearchable && input !== debouncedQuery);

  useEffect(() => {
    if (!debouncedQuery.trim() || debouncedQuery.startsWith("http")) {
      setTankResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    apiClient({
      endpoint: "search-skills",
      token,
      payload: { query: debouncedQuery },
    })
      .then(({ tankResults: tank }) => {
        setTankResults(tank ?? []);
      })
      .catch(() => {
        setTankResults([]);
      })
      .finally(() => setSearching(false));
  }, [debouncedQuery, token]);

  const install = (sourceUrl: string) => {
    setInput("");
    setTankResults([]);
    toast.promise(
      apiClient({
        endpoint: "install-skill",
        token,
        payload: { botId, sourceUrl },
      }),
      {
        loading: "Installing skill...",
        success: ({ name }) => `Installed ${name ?? sourceUrl}`,
        error: (err) => `Failed: ${err.error ?? err.message}`,
      },
    );
  };

  const installFromInput = () => {
    const v = input.trim();
    if (v) install(v);
  };

  const uninstall = async (skillId: string, sourceUrl: string) => {
    if (!await customConfirm(`Uninstall skill ${sourceUrl}?`)) return;
    toast.promise(
      apiClient({
        endpoint: "uninstall-skill",
        token,
        payload: { botId, skillId },
      }),
      {
        loading: "Uninstalling...",
        success: "Skill uninstalled",
        error: (err) => `Failed: ${err.error ?? err.message}`,
      },
    );
  };

  const content = (
    <div className="flex flex-col gap-4">
      <div className="text-sm text-text-secondary-light dark:text-text-secondary">
        Search verified skills from Tank, or paste a GitHub URL / npm package
        name to install directly.
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onInput={(e) => setInput(e.currentTarget.value)}
          placeholder="Search Tank skills or paste a GitHub URL..."
          className={`flex-1 ${inputBaseClass}`}
        />
        <Button onClick={installFromInput} disabled={!input.trim()}>
          Install
        </Button>
      </div>
      {showSearching && (
        <div className="text-sm text-text-tertiary-light dark:text-text-tertiary py-2 text-center">
          Searching...
        </div>
      )}
      {!showSearching && tankResults.length > 0 && (
        <div className="flex flex-col border border-border-subtle-light dark:border-border-subtle rounded bg-surface-raised-light dark:bg-surface-raised max-h-80 overflow-y-auto">
          {tankResults.map((r) => (
            <TankResultRow
              key={r.name}
              result={r}
              onInstall={() => install(`tank:${r.name}`)}
            />
          ))}
        </div>
      )}
      {skills.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="font-medium text-sm">Installed skills</div>
          {skills.map((s) => (
            <SkillRow
              key={s.id}
              sourceUrl={s.sourceUrl}
              installedAt={s.installedAt}
              version={s.version}
              onUninstall={() => uninstall(s.id, s.sourceUrl)}
            />
          ))}
        </div>
      )}
    </div>
  );

  return hideCollapsible ? content : (
    <Collapsible title="Community Skills">
      {content}
    </Collapsible>
  );
};
